/**
 * AES-256-GCM payload encryption for the API gateway.
 *
 * Handshake (once per session):
 *   1. Generate random AES-256 key via Web Crypto
 *   2. Encrypt AES key with hardcoded RSA-OAEP public key
 *   3. POST /crypto/session-init { encryptedKey } → sessionId
 *
 * Every subsequent request/response:
 *   - Encrypt body  : AES-GCM → { data } — data = Base64(iv || ciphertext)
 *   - Decrypt body  : { data } → split IV back off the front → plaintext JSON
 *   - Header        : X-Session-Id: <sessionId>
 *
 * IV is prepended to the ciphertext (one field on the wire) rather than sent
 * as a separate {iv, data} pair — matches api-auth-gateway's PayloadCipherService.
 * It isn't secret, but it still has to be unique per encryption under the same
 * session key (GCM nonce reuse breaks confidentiality + the auth tag), so a
 * fresh random IV is generated every call regardless of wire shape.
 *
 * Public key is hardcoded (not fetched) — eliminates the /crypto/public-key
 * network call and removes any risk of key substitution in transit.
 *
 * !! MUST match whichever gateway REACT_APP_GATEWAY_URL currently points at !!
 * api-auth-gateway's local (application-local.properties) and Consul-dev
 * keypairs are DIFFERENT — pointing at one gateway while hardcoding the
 * other's public key makes /crypto/session-init fail (RSA decrypt on the
 * gateway throws, since it's the wrong keypair). Below is the Consul-dev
 * keypair's public key — the real gateway is deployed via Consul, not run
 * from application-local.properties, so this is the one that matters.
 */

const GATEWAY_BASE = process.env.REACT_APP_GATEWAY_URL;

// RSA-2048 public key (SPKI/Base64) — matches api-auth-gateway's Consul-dev
// gateway.rsa.public-key, same key already used by admin-portal-frontend's
// security branch (one gateway instance, one keypair, both frontends).
const RSA_PUBLIC_KEY =
  'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAvXe6fsGSyNfPUFCTBrNT' +
  'E34ktWrWJie5t9Bj1liIaByjhiryyGvI1YIYrJU/RT9riobZkUTAy5VqQSnvMWyt' +
  'd0m1qHEmnrK1gRobbHF3N5MoTERxWGDzOLgFNg/NkLI9ZtjHOaWbrI2AcjMTKJm6' +
  'YXDx4AmK9iL9CPdjzJbvr9qN5PvOpdyBMVjpnE87PrD8s9RJhJ2/RttyCPuOLzad' +
  'goHY3T2mJDeO0m2WTG7/Z5Zuq0KfGVWAyeh+bz1Pj24ZEnH2npalvr0HGE1HJEE7' +
  'YGyclewRA7LtehPVv49a3rU/w7XwWnpumHCh2u9Ay8dTLV0DB11DCjqCWYH0Jrto' +
  '5wIDAQAB';

// In-memory session state (not localStorage — key material should not persist)
let _aesKey    = null;   // CryptoKey
let _aesRaw    = null;   // Uint8Array (raw bytes for GCM ops)
let _sessionId = null;

// ── Helpers ───────────────────────────────────────────────────────────────────

function b64ToBytes(b64) {
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

function bytesToB64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}

// ── AES-GCM ──────────────────────────────────────────────────────────────────

const IV_LENGTH_BYTES = 12;

async function aesEncrypt(plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const encoded = new TextEncoder().encode(
    typeof plaintext === 'string' ? plaintext : JSON.stringify(plaintext)
  );
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    _aesKey,
    encoded
  ));

  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);

  return { data: bytesToB64(combined) };
}

async function aesDecrypt(data) {
  const combined = b64ToBytes(data);
  const iv = combined.slice(0, IV_LENGTH_BYTES);
  const ciphertext = combined.slice(IV_LENGTH_BYTES);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    _aesKey,
    ciphertext
  );
  return new TextDecoder().decode(plaintext);
}

// ── RSA-OAEP ─────────────────────────────────────────────────────────────────

async function rsaEncryptKey(rsaPubKeyB64, aesKeyBytes) {
  const keyBytes = b64ToBytes(rsaPubKeyB64);
  const rsaKey = await crypto.subtle.importKey(
    'spki',
    keyBytes,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );
  const encrypted = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    rsaKey,
    aesKeyBytes
  );
  return bytesToB64(new Uint8Array(encrypted));
}

// ── Session init ──────────────────────────────────────────────────────────────

let _initInFlight = null;

// Several requests can fire at once on page load, each finding no session. They must share ONE handshake:
// separate ones overwrite each other's key, and a response encrypted with the losing key can never be decrypted.
export function initSession() {
  if (!_initInFlight) {
    _initInFlight = doInitSession().finally(() => { _initInFlight = null; });
  }
  return _initInFlight;
}

async function doInitSession() {
  // 1. Generate AES-256 key (kept local until the gateway has accepted it, so the key and session id
  //    in use never disagree while a handshake is pending)
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', key));

  // 2. Encrypt AES key with hardcoded RSA public key
  const encryptedKey = await rsaEncryptKey(RSA_PUBLIC_KEY, raw);

  // 3. POST /crypto/session-init — this endpoint is NOT payload-encrypted
  const siRes = await fetch(`${GATEWAY_BASE}/crypto/session-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ encryptedKey }),
  });
  if (!siRes.ok) throw new Error('Session init failed');
  const { sessionId } = await siRes.json();
  _aesKey = key;
  _aesRaw = raw;
  _sessionId = sessionId;

  return sessionId;
}

export function getSessionId()   { return _sessionId; }
export function hasSession()     { return !!_aesKey && !!_sessionId; }

export async function endSession() {
  if (!_sessionId) return;
  await fetch(`${GATEWAY_BASE}/crypto/session/${_sessionId}`, { method: 'DELETE' });
  _aesKey = null; _aesRaw = null; _sessionId = null;
}

// ── Encrypt / decrypt for axios interceptors ──────────────────────────────────

export async function encryptPayload(data) {
  if (!_aesKey) throw new Error('No active session');
  return aesEncrypt(data);
}

export async function decryptPayload(envelope) {
  if (!_aesKey) throw new Error('No active session');
  if (!envelope || !envelope.data) return envelope;
  const plaintext = await aesDecrypt(envelope.data);
  try { return JSON.parse(plaintext); } catch { return plaintext; }
}
